import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { RichTextEditor } from '../../components/RichTextEditor';
import { TextField } from '../../components/Field';
import { CustomFieldInputs } from '../customfields/CustomFieldInputs';
import { ArticleSuggestions } from '../knowledge/ArticleSuggestions';
import { DuplicateHints } from './DuplicateHints';
import { useCustomFields } from '../customfields/queries';
import type { ComboboxOption } from '../../components/Combobox';
import { CustomerCombobox } from '../crm/CustomerCombobox';
import { customerOption } from '../crm/customerOption';
import { useCustomer, useCustomerList } from '../crm/queries';
import { useCategories } from '../masterdata/queries';
import { useTemplates } from '../shortcuts/queries';
import { useCreateTicket } from './queries';
import { flattenCategories } from './presentation';
import { Select } from '../../components/Select';

/**
 * Ticketanlage.
 *
 * Bewusst schlank: Betreff und Sachverhalt. Warteschlange, Typ und Prioritaet
 * kommen aus den Vorgaben des Mandanten -- wer ein Ticket am Telefon
 * aufnimmt, soll tippen koennen und nicht vier Auswahlfelder bedienen. Ab
 * Phase 1 kommt die vollstaendige Maske daneben.
 *
 * Der Dialog stammt von Radix und bringt keine eigene Gestaltung mit
 * (docs/02, Abschnitt 5) -- aber Fokusfalle, Escape-Behandlung und die
 * richtigen ARIA-Rollen, die man von Hand regelmaessig falsch baut.
 */
const schema = z.object({
  title: z.string().min(1, 'tickets.subjectRequired').min(3, 'tickets.subjectTooShort'),
  // Das HTML des Editors. Der Klartext daneben entsteht im Server -- zwei
  // Quellen fuer denselben Satz waeren zwei Staende.
  description_html: z.string(),
});

type FormValues = z.infer<typeof schema>;

interface CreateTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (ticketId: string) => void;
  /**
   * Der Kunde, der im Ticketmodul gerade gewaehlt ist -- F1-56. Er wird
   * vorbelegt, bleibt aber aenderbar: Wer bei Kunde X ist und einen Anruf
   * von Kunde Y aufnimmt, soll nicht erst die Liste umstellen muessen.
   */
  initialCustomerId?: string;
}

export function CreateTicketDialog({
  open,
  onOpenChange,
  onCreated,
  initialCustomerId,
}: CreateTicketDialogProps) {
  const { t } = useTranslation();
  const createTicket = useCreateTicket();
  const categories = useCategories();
  const customFields = useCustomFields('ticket', true);
  const templates = useTemplates();
  // Der Kunde ist am Ticket Pflicht. Fuehrt der Mandant genau einen --
  // der Regelfall bei ITsense --, entscheidet ihn der Server, und das
  // Auswahlfeld bleibt aus. Bei mehreren verlangt er die Angabe.
  //
  // Gefragt werden hoechstens zwei: Mehr braucht die Frage "genau einer?"
  // nicht. Die Auswahl selbst sucht beim Server und ist nicht auf eine
  // Seite beschraenkt -- die Aufklappliste davor endete nach 25 Kunden.
  const customers = useCustomerList({ status: 'active', limit: 2 });

  const [categoryId, setCategoryId] = useState('');
  // ``undefined`` heisst "nicht angefasst" und laesst die Vorbelegung
  // stehen; ``null`` heisst "ausdruecklich geleert". Ohne die Unterscheidung
  // saesse der vorbelegte Kunde nach dem Leeren sofort wieder im Feld.
  const [customer, setCustomer] = useState<ComboboxOption | null | undefined>(undefined);
  const preset = useCustomer(initialCustomerId ?? '');
  const [templateId, setTemplateId] = useState('');
  const [customValues, setCustomValues] = useState<Record<string, unknown>>({});

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', description_html: '' },
  });

  /**
   * Uebernimmt die Vorbelegung einer Vorlage in die Maske.
   *
   * Sichtbar und nicht erst beim Absenden: Der Bearbeiter soll sehen, was die
   * Vorlage einsetzt, und es aendern koennen. Der Server fuehrt beide Seiten
   * noch einmal zusammen -- was hier steht, gewinnt dort gegen die Vorlage.
   */
  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const template = templates.data?.find((candidate) => candidate.id === id);
    if (!template) return;

    const defaults = template.defaults as Record<string, unknown>;
    if (typeof defaults.title === 'string') setValue('title', defaults.title);
    // Eine Vorlage bringt ihren Sachverhalt als Klartext mit. Er geht als
    // Klartext in den Editor -- der macht daraus einen Absatz.
    if (typeof defaults.description === 'string')
      setValue('description_html', defaults.description);
    if (typeof defaults.category_id === 'string') setCategoryId(defaults.category_id);
  };

  const customerOptions = customers.data?.pages.flatMap((page) => page.items) ?? [];

  // Bei genau einem Kunden ist die Auswahl keine: Er wird vorgewaehlt, damit
  // das Feld zeigt, wohin das Ticket geht, ohne einen Klick zu verlangen.
  const onlyCustomer =
    customerOptions.length === 1 && customerOptions[0] ? customerOption(customerOptions[0]) : null;
  // Nur ein aktiver Kunde wird vorbelegt: Die Liste darf einen ruhenden
  // zeigen, die Anlage bietet aber nur aktive an (``status="active"`` am
  // Auswahlfeld). Eine Vorbelegung daneben waere ein Weg an dieser Grenze
  // vorbei, den niemand bewusst gewaehlt hat.
  const presetCustomer = preset.data?.status === 'active' ? customerOption(preset.data) : null;
  const chosenCustomer =
    customer === undefined ? (presetCustomer ?? onlyCustomer) : (customer ?? onlyCustomer);
  const effectiveCustomerId = chosenCustomer?.id ?? '';

  // Fuehrt der Mandant mehrere Kunden, ist die Angabe Pflicht -- der Server
  // raet nicht, welcher gemeint ist. Solange die Liste noch laedt, bleibt das
  // Absenden gesperrt: Andernfalls schickte ein schneller Klick den Antrag
  // ohne Kunden ab, das Auswahlfeld erschiene eine Zehntelsekunde spaeter,
  // und der Anwender saehe einen Fehler zu einem Feld, das er nie gesehen hat.
  const customerMissing = customers.isPending || !effectiveCustomerId;

  const onSubmit = handleSubmit((values) => {
    // Die Herkunft wird ausdruecklich gesetzt und nicht der Vorgabe des
    // Servers ueberlassen: Ein Ticket, das eine Bearbeiterin hier von Hand
    // anlegt, stammt in aller Regel aus einem Anruf. Die Angabe steuert
    // spaeter Auswertungen nach Kanal (F5-06) -- eine falsche Vorgabe waere
    // dort nicht mehr zu korrigieren.
    createTicket.mutate(
      {
        ...values,
        source: 'phone',
        ...(categoryId ? { category_id: categoryId } : {}),
        ...(effectiveCustomerId ? { customer_id: effectiveCustomerId } : {}),
        ...(templateId ? { template_id: templateId } : {}),
        custom_fields: customValues,
      },
      {
        onSuccess: (ticket) => {
          reset();
          setCategoryId('');
          setCustomer(undefined);
          setTemplateId('');
          setCustomValues({});
          onOpenChange(false);
          onCreated?.(ticket.id);
        },
      },
    );
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>{t('tickets.createTitle')}</h2>
          </Dialog.Title>
          <Dialog.Description className="visually-hidden">
            {t('tickets.descriptionPlaceholder')}
          </Dialog.Description>

          {createTicket.isError ? <ErrorNotice error={createTicket.error} /> : null}

          <form onSubmit={onSubmit} noValidate>
            {/* Der Kunde steht immer da -- auch wenn es nur einen gibt. Ein
                Ticket gehoert zu einem Kunden, und wer es anlegt, soll sehen
                zu welchem. Bei genau einem ist er vorgewaehlt; bei mehreren
                verlangt das Feld eine Entscheidung, statt die erste zu
                treffen. */}
            {customerOptions.length > 0 ? (
              <div className="field">
                <label className="field__label" htmlFor="ticket-customer">
                  {t('crm.customer')}
                </label>
                <CustomerCombobox
                  id="ticket-customer"
                  status="active"
                  value={chosenCustomer}
                  onChange={setCustomer}
                  clearable={onlyCustomer === null}
                  placeholder={t('crm.chooseCustomer')}
                />
              </div>
            ) : null}

            {/* Die Vorlage steht vor dem Betreff, weil sie ihn vorbelegen
                kann. Darunter waere sie eine Korrektur dessen, was der
                Bearbeiter schon getippt hat (F1-37, K-19). */}
            {templates.data && templates.data.length > 0 ? (
              <div className="field">
                <label className="field__label" htmlFor="ticket-template">
                  {t('templates.choose')}
                </label>
                <Select
                  id="ticket-template"
                  className="field__control"
                  value={templateId}
                  onChange={(event) => {
                    applyTemplate(event.target.value);
                  }}
                >
                  <option value="">{t('templates.none')}</option>
                  {templates.data
                    .filter((template) => template.is_active)
                    .map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                </Select>
                <span className="field__hint">{t('templates.chooseHint')}</span>
              </div>
            ) : null}

            <TextField
              label={t('tickets.subject')}
              placeholder={t('tickets.subjectPlaceholder')}
              autoFocus
              error={errors.title ? t(errors.title.message ?? '') : undefined}
              {...register('title')}
            />
            {/* Derselbe Editor wie am Beitrag. Ohne Bild: Beim Anlegen
                gibt es das Ticket noch nicht, an dem ein Anhang haengen
                koennte -- ein Bildknopf zeigte ins Leere. */}
            <div className="field">
              <label className="field__label" htmlFor="create-description">
                {t('tickets.description')}
              </label>
              <RichTextEditor
                id="create-description"
                value={watch('description_html')}
                onChange={(html) => {
                  setValue('description_html', html);
                }}
                ariaLabel={t('tickets.description')}
                placeholder={t('tickets.descriptionPlaceholder')}
                features={{ image: false }}
              />
            </div>

            {/* Loesungsvorschlaege waehrend der Erfassung -- F4-13, K-107.
                Sie stehen unter dem Sachverhalt und nicht daneben: Wer den
                Betreff schreibt, liest von oben nach unten weiter. */}
            <ArticleSuggestions text={watch('title')} />

            {/* Moegliche Duplikate -- F1-28, K-24. Neben den
                Loesungsvorschlaegen, weil beide dieselbe Frage stellen:
                Gibt es das schon? */}
            <DuplicateHints title={watch('title')} />

            {categories.data && categories.data.length > 0 ? (
              <div className="field">
                <label className="field__label" htmlFor="ticket-category">
                  {t('tickets.category')}
                </label>
                <Select
                  id="ticket-category"
                  className="field__control"
                  value={categoryId}
                  onChange={(event) => {
                    setCategoryId(event.target.value);
                  }}
                >
                  <option value="">{t('tickets.noCategory')}</option>
                  {flattenCategories(categories.data).map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}

            <CustomFieldInputs
              definitions={customFields.data ?? []}
              values={customValues}
              onChange={(key, value) => {
                setCustomValues((current) => ({ ...current, [key]: value }));
              }}
            />

            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Dialog.Close asChild>
                <Button variant="ghost">{t('app.cancel')}</Button>
              </Dialog.Close>
              <Button
                type="submit"
                variant="primary"
                disabled={customerMissing}
                busy={createTicket.isPending}
              >
                {t('tickets.newTicket')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
